import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import useCallApi from "../../hooks/useCallApi";
import { Routes } from "../../routes/Routes";
import { useAlert } from "../../context/AlertContext";
import { useFormik } from "formik";
import parkingValidationSchema from "../../validations/directory/parking";
import directory from "../../validations/directory";
import DirectoryApi from "../../api/directory";
import { useContent } from "../../content/useContent";
import { useRTL } from "../../hooks/useLanguage/useRTL";

type DeletedImageType = "image" | "logo" | "hero" | "";

const useDirectoryContainer = (id?: any, isEdit: any = false) => {
  const navigate = useNavigate();
  const { callApi, loading } = useCallApi();
  const { setSuccessMessage, setErrorMessage } = useAlert();

  const { schema, pages, general } = useContent();
  const { isRTL } = useRTL();

  const { directories } = pages;
  const [pageLoading, setPageLoading] = useState(true);
  const [openDeleteImage, setOpenDeleteImage] = useState(false);

  const [deletedImageType, setDeletedImageType] =
    useState<DeletedImageType>("");

  const [initialValues, setInitialValues] = useState({
    name: "",
    nameAr: "",
    address: "",

    status: true,
    longitude: 0,
    latitude: 0,
    POI: "",
    openingHours: "",
    openingHoursAr: "",

    website: "",
    phone: "",
    meta: "",
    metaAr: "",
    reservable: true,
    comingSoon: false,
    category: "",
    parking: [],
    penguinID: null
  });

  const [images, setImages] = useState<any>([]);
  const [logo, setLogo] = useState<any>([]);
  const [heroImage, setHeroImage] = useState<any>([]);
  const [parking, setParking] = useState<any>([]);

  const [center, setCenter] = useState({ lat: 0, lng: 0 });
  const [zoom, setZoom] = useState(5);

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openParkingDialog, setOpenParkingDialog] = useState(false);
  const [openDeleteParkingDialog, setOpenDeleteParkingDialog] = useState(false);
  const [selected, setSelected] = useState<any>(null);

  const [selectedImage, setSelectedImage] = useState<any>(null);

  const handleOpenDeleteParking = (parking: any) => {
    setSelected(parking);
    setOpenDeleteParkingDialog(true);
  };

  const handleDeleteParking = () => {
    setSelected(null);
    setOpenDeleteParkingDialog(false);
    setParking(parking.filter((park: string) => park !== selected));
  };

  useEffect(() => {
    if (id) {
      setPageLoading(true);

      const API = isEdit
        ? DirectoryApi.getOneBoth(id)
        : DirectoryApi.getOne(id);

      callApi(API, (directory: any) => {
        if (isEdit) {
          setInitialValues({
            ...directory,
            name: directory.name.en,
            nameAr: directory.name.ar,

            openingHours: directory.openingHours.en,
            openingHoursAr: directory.openingHours.ar,

            meta: directory.meta?.en,
            metaAr: directory.meta?.ar,

            status: directory.status === "Available",
            reservable: directory.reservable === "Available",
          });
          setParking(directory.parking);
        } else {
          setInitialValues({
            ...directory,
            status: directory.status === "Available",
            reservable: directory.reservable === "Available",
          });
          setParking(directory.parking);
        }

        setImages(directory.images);
        setLogo([directory.logo]);
        setHeroImage(directory.heroImage ? [directory.heroImage] : []);
        setCenter({ lat: directory.latitude, lng: directory.longitude });

        setPageLoading(false);
      });
    } else {
      setPageLoading(false);
    }
  }, [id, isRTL]);

  const handleDelete = () => {
    callApi(DirectoryApi.delete(id), () => {
      setOpenDeleteDialog(false);
      navigate(Routes.directories);
      setTimeout(() => setSuccessMessage(directories.deleteDir), 1000);
    });
  };

  const handleSubmit = (values: any) => {
    let body = { ...values, parking };

    delete body.id;
    delete body.createdAt;
    delete body.updatedAt;
    body.name = {
      en: values.name,
      ar: values.nameAr,
    };

    body.openingHours = {
      en: values.openingHours,
      ar: values.openingHoursAr,
    };

    body.meta = {
      en: values.meta,
      ar: values.metaAr,
    };

    delete body.nameAr;
    delete body.openingHoursAr;
    delete body.metaAr;
    delete body.loyaltyCategory;

    body.status = values.status ? "Available" : "Not Available";
    body.reservable = values.reservable ? "Available" : "Not Available";
    body.images = images.map((image: any) => {
      return { url: image.url, thumbnail: image.thumbnail };
    });
    body.logo = logo[0];
    body.heroImage = heroImage[0];

    if (values.POI === "") {
      delete body.POI;
    }

    const successMessage = id ? directories.updateDir : directories.createDir;

    if (id) {
      callApi(DirectoryApi.update(id, body), () => {
        setTimeout(() => setSuccessMessage(successMessage), 1000);
        navigate(Routes.getDirectory(id));
      });
    } else {
      callApi(DirectoryApi.create(body), (result: any) => {
        setTimeout(() => setSuccessMessage(successMessage), 1000);
        navigate(Routes.getDirectory(result.id));
      });
    }
  };

  const formik: any = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: () => directory(schema),
    onSubmit: (values: any) => handleSubmit(values),
  });

  const handleAddParking = (values: any, resetForm: any) => {
    if (parking.some((park: any) => park === values.parking)) {
      setErrorMessage(directories.parkingExists);
    } else {
      setParking([...parking, values.parking]);
      setOpenParkingDialog(false);
      resetForm();
    }
  };

  const parkingFormik: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      parking: "",
    },
    validationSchema: () => parkingValidationSchema(schema),
    onSubmit: (values: any, { resetForm }: any) =>
      handleAddParking(values, resetForm),
  });

  const handleMarker = (e: any) => {
    formik.setFieldValue("latitude", e.latLng.lat());
    formik.setFieldValue("longitude", e.latLng.lng());
  };

  const handleSearch = (lat: any, lng: any) => {
    formik.setFieldValue("latitude", lat);
    formik.setFieldValue("longitude", lng);
  };

  const handleFile = (event: any) => {
    const files = event.target.files;
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append("images", files[i]);
    }
    callApi(DirectoryApi.upload(formData), async (data: any) => {
      setSuccessMessage(general.uploadImages);
      setImages([...images, ...data]);
    });
  };

  const handleLogo = (event: any) => {
    const file = event.target.files[0];
    const formData = new FormData();
    formData.append("images", file);
    callApi(DirectoryApi.upload(formData), async (data: any) => {
      setSuccessMessage(directories.uploadLogo);
      setLogo(data);
    });
  };

  const handleHeroImage = (event: any) => {
    const file = event.target.files[0];
    const formData = new FormData();
    formData.append("images", file);
    callApi(DirectoryApi.upload(formData), async (data: any) => {
      setSuccessMessage(directories.uploadHeroImage);
      setHeroImage(data);
    });
  };

  const handleOpenDeleteImage = (image: any) => {
    setOpenDeleteImage(true);
    setSelectedImage(image);
    setDeletedImageType("image");
  };

  const handleOpenDeleteLogo = (image: any) => {
    setOpenDeleteImage(true);
    setSelectedImage(image);
    setDeletedImageType("logo");
  };

  const handleOpenDeleteHeroImage = (image: any) => {
    setOpenDeleteImage(true);
    setSelectedImage(image);
    setDeletedImageType("hero");
  };

  const handleDeleteImage = () => {
    if (selectedImage.id) {
      callApi(DirectoryApi.deleteImage(selectedImage.id), async (data: any) => {
        setSuccessMessage(general.deleteImage);
        if (deletedImageType === "hero") {
          setHeroImage([]);
        } else if (deletedImageType === "logo") {
          setLogo([]);
        } else {
          setImages(
            images.filter((image: any) => image.id !== selectedImage.id)
          );
        }
        setSelectedImage({ id: "" });

        setOpenDeleteImage(false);
      });
    } else {
      callApi(
        DirectoryApi.deleteImageSource({ url: selectedImage.url }),
        async () => {
          setSuccessMessage(general.deleteImage);
          if (deletedImageType === "hero") {
            setHeroImage([]);
          } else if (deletedImageType === "logo") {
            setLogo([]);
          } else {
            setImages(
              images.filter((image: any) => image.url !== selectedImage.url)
            );
          }
          setSelectedImage(null);

          setOpenDeleteImage(false);
        }
      );
    }
  };

  return {
    directory: initialValues,
    pageLoading,

    formik,
    center,
    handleMarker,
    handleSearch,
    setCenter,
    zoom,
    setZoom,

    handleDelete,
    openDeleteDialog,
    setOpenDeleteDialog,
    loading,

    handleFile,

    images,

    handleLogo,
    logo,

    openDeleteImage,
    setOpenDeleteImage,

    handleOpenDeleteImage,

    handleDeleteImage,
    handleOpenDeleteLogo,

    heroImage,
    handleOpenDeleteHeroImage,
    handleHeroImage,

    parkingFormik,
    setOpenParkingDialog,
    openParkingDialog,
    handleOpenDeleteParking,
    parking,
    handleDeleteParking,
    openDeleteParkingDialog,
    setOpenDeleteParkingDialog,
    selected,
  };
};

export default useDirectoryContainer;
